/**
 * <sm-design-card> — the ONE sendmoments design card (NGE-9805).
 *
 * Loaded by every listing surface: sm-design-grid, sm-related-designs,
 * sm-design-set. A section only emits the tag; the card hydrates itself.
 *
 *   <sm-design-card unit="wedding-invitation-blossom-arch" label="Blossom Arch"
 *                   variant="full" cta="Design now"></sm-design-card>
 *
 * variant 'full'    — PLP: stage, photo dots, format silhouettes, title, CTA
 * variant 'compact' — recommendation bands: stage, title (default)
 *
 * Data: /apps/sm/unit-structure, fetched when the card nears the viewport —
 * the same source the PDP charges from, so a card can never disagree with it.
 * A unit the engine refuses hides its own card.
 *
 * Styles: the `.sm-card__*` family, owned by sections/sm-design-grid.liquid.
 */
class SmDesignCard extends HTMLElement {
  connectedCallback() {
    this.unit = this.getAttribute('unit');
    this.variant = this.getAttribute('variant') || 'compact';
    if (!this.unit) return;
    this.renderShell();
    this.observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * Title, links and layout are known up front; only the imagery waits on data.
   */
  renderShell() {
    // Unit-grain product PDP when the projector made one (grain ruling
    // 2026-08-21); the pilot metaobject page is the fallback until it retires.
    const productHandle = this.getAttribute('product-handle');
    const href = productHandle
      ? `/products/${encodeURIComponent(productHandle)}`
      : `/pages/designs/${encodeURIComponent(this.unit)}`;
    const label = this.escape(this.getAttribute('label') || this.unit);
    const cta = this.getAttribute('cta');
    const full = this.variant === 'full';
    this.innerHTML = `
      <div class='sm-card__stage'>
        <a class='sm-card__stage-link' href='${href}' aria-label='${label}'>
          <img class='sm-card__img' alt='${label}' loading='lazy' width='600' height='600'>
        </a>
        <button type='button' class='sm-card__wish' aria-label='Add to wishlist'>
          <svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='currentColor' stroke-width='1.8'><path d='M12 20.3 4.7 13a4.9 4.9 0 0 1 0-7 4.9 4.9 0 0 1 7 0l.3.3.3-.3a4.9 4.9 0 0 1 7 0 4.9 4.9 0 0 1 0 7L12 20.3z'/></svg>
        </button>
        ${full ? "<div class='sm-card__dots' role='radiogroup' aria-label='Views'></div>" : ''}
      </div>
      ${full ? "<div class='sm-card__sizes' role='radiogroup' aria-label='Formats'></div>" : ''}
      <a class='sm-card__name' href='${href}'>${label}</a>
      ${full && cta ? `<a class='sm-card__cta' href='${href}'>${this.escape(cta)}</a>` : ''}
    `;
    this.img = this.querySelector('.sm-card__img');
  }

  /**
   * Hydrate once, when the card comes near the viewport.
   */
  observe() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.hydrate();
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  async hydrate() {
    try {
      const response = await fetch(`/apps/sm/unit-structure?unit=${encodeURIComponent(this.unit)}`);
      const data = await response.json();
      if (!data.ok) {
        this.style.display = 'none';
        return;
      }
      this.sizes = (data.sizes || []).filter((size) => size.engine_size_id);
      if (!this.sizes.length) {
        this.style.display = 'none';
        return;
      }
      if (this.variant === 'full') this.renderControls();
      this.showPhoto(0);
    } catch {
      // Proxy unreachable: the card stays title-only rather than vanishing.
    }
  }

  /**
   * Dots page photos OF THE SAME format; silhouettes switch the format. The
   * payload carries exactly one render per format today, so the photo list has
   * one entry and the dots stay hidden — they appear by themselves once the
   * engine exposes real multi-view shots.
   */
  renderControls() {
    this.photos = this.sizes[0].thumb ? [this.sizes[0].thumb] : [];
    const dots = this.querySelector('.sm-card__dots');
    if (this.photos.length > 1) {
      dots.innerHTML = this.photos
        .map(
          (_, i) =>
            `<button type='button' class='sm-card__dot${i === 0 ? ' is-current' : ''}' role='radio' aria-checked='${i === 0}' aria-label='View ${i + 1}' data-photo-idx='${i}'></button>`
        )
        .join('');
      dots.addEventListener('click', (event) => {
        const button = event.target.closest('[data-photo-idx]');
        if (!button) return;
        event.preventDefault();
        this.showPhoto(parseInt(button.dataset.photoIdx, 10));
      });
    }

    const strip = this.querySelector('.sm-card__sizes');
    strip.innerHTML = this.sizes
      .map(
        (size, i) =>
          `<button type='button' class='sm-card__size${i === 0 ? ' is-current' : ''}' role='radio' aria-checked='${i === 0}' aria-label='${this.escape(size.name || size.engine_size_id)}' data-size-idx='${i}'>` +
          `<img src='${this.sizeIcon(size.engine_size_id)}' alt='' loading='lazy' width='33' height='33'>` +
          '</button>'
      )
      .join('');
    // Hovering a silhouette SELECTS it: the format stays picked and its render
    // stays on stage after the cursor leaves. Click is the touch path.
    const pick = (event) => {
      const button = event.target.closest('[data-size-idx]');
      if (!button) return;
      event.preventDefault();
      this.pickSize(parseInt(button.dataset.sizeIdx, 10));
    };
    strip.addEventListener('mouseover', pick);
    strip.addEventListener('click', pick);
  }

  showPhoto(index) {
    const source =
      this.variant === 'full' ? this.photos : this.sizes.map((s) => s.thumb).filter(Boolean);
    if (source?.[index]) this.img.src = source[index];
    this.mark('.sm-card__dot', index);
  }

  pickSize(index) {
    const thumb = this.sizes[index]?.thumb;
    if (thumb) this.img.src = thumb;
    this.mark('.sm-card__size', index);
  }

  mark(selector, index) {
    this.querySelectorAll(selector).forEach((element, i) => {
      element.classList.toggle('is-current', i === index);
      element.setAttribute('aria-checked', i === index ? 'true' : 'false');
    });
  }

  sizeIcon(id) {
    return `https://static.sendmoments.de/products/sizes/${encodeURIComponent(id)}_b.svg`;
  }

  escape(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }
}

if (!customElements.get('sm-design-card')) {
  customElements.define('sm-design-card', SmDesignCard);
}
